import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Library, Search, Image as ImageIcon, Inbox, Mail, Sparkles,
  AlertCircle, ArrowRight, Loader2, TrendingUp, TrendingDown, Minus,
  Phone, Handshake, ShoppingBag, MessageSquareQuote,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useAdminTheme, tc } from './theme';

/* ------------------------------ helpers ----------------------------- */
function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-GB');
}

/* ------------------------------ Sparkline --------------------------- */
function Sparkline({ values = [], stroke = '#a855f7' }) {
  const safe = (values || []).map((v) => Number(v) || 0);
  if (safe.length < 2) return null;
  const max = Math.max(...safe, 1);
  const w = 100, h = 28;
  const dx = w / (safe.length - 1);
  const points = safe.map((v, i) => `${i * dx},${h - (v / max) * (h - 2) - 1}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-7" aria-hidden>
      <polyline fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
}

/* ------------------------------ Trend chip -------------------------- */
function TrendChip({ delta }) {
  if (delta === null || delta === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-500">
        <Minus size={10} /> new
      </span>
    );
  }
  const isUp = delta >= 0;
  const cls = isUp
    ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
    : 'bg-rose-50 text-rose-700 border border-rose-100';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md ${cls}`}>
      {isUp ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {Math.abs(delta)}% wow
    </span>
  );
}

/* ------------------------------ Metric card ------------------------- */
function MetricCard({ theme, label, value, sub, icon: Icon, to, delta, spark, sparkStroke }) {
  return (
    <Link to={to} className={`group relative overflow-hidden rounded-2xl p-5 transition-all hover:border-violet-500/40 ${tc.cardBg(theme)}`}>
      <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 blur-2xl group-hover:scale-110 transition-transform" />
      <div className="relative flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30 text-white">
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] uppercase tracking-widest font-semibold ${tc.textMuted(theme)}`}>{label}</p>
          <div className="flex items-end gap-2 mt-0.5">
            <p className={`text-3xl font-extrabold ${tc.textPrimary(theme)}`}>{value}</p>
            {delta !== undefined && <TrendChip delta={delta} />}
          </div>
          {sub && <p className={`text-xs mt-0.5 ${tc.textMuted(theme)}`}>{sub}</p>}
          {spark && spark.length > 1 && (
            <div className="mt-2"><Sparkline values={spark} stroke={sparkStroke || '#a855f7'} /></div>
          )}
        </div>
        <ArrowRight size={16} className={`${tc.textDim(theme)} group-hover:text-fuchsia-500 transition-colors`} />
      </div>
    </Link>
  );
}

/* ------------------------------ Activity feed ----------------------- */
const KIND_META = {
  lead:       { Icon: Phone,          label: 'Lead',        bg: 'bg-fuchsia-500' },
  order:      { Icon: ShoppingBag,    label: 'Order',       bg: 'bg-amber-500' },
  partner:    { Icon: Handshake,      label: 'Partner',     bg: 'bg-sky-500' },
  newsletter: { Icon: Mail,           label: 'Newsletter',  bg: 'bg-emerald-500' },
};

function ActivityList({ items = [], theme }) {
  if (!items.length) {
    return <p className={`text-sm ${tc.textMuted(theme)}`}>No recent activity yet.</p>;
  }
  return (
    <ul className="divide-y divide-slate-100 dark:divide-white/5">
      {items.map((it, idx) => {
        const meta = KIND_META[it.kind] || KIND_META.lead;
        return (
          <li key={`${it.kind}-${it.at}-${idx}`} className="py-3 flex items-center gap-3">
            <span className={`w-8 h-8 rounded-lg ${meta.bg} text-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <meta.Icon size={14} />
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${tc.textPrimary(theme)}`}>{it.label}</p>
              <p className={`text-[11px] truncate ${tc.textMuted(theme)}`}>
                {meta.label}{it.email && it.email !== it.label ? ` · ${it.email}` : ''}{it.status ? ` · ${it.status}` : ''}
              </p>
            </div>
            <span className={`text-[11px] flex-shrink-0 ${tc.textDim(theme)}`}>{timeAgo(it.at)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------ Component --------------------------- */
export default function AdminV2Dashboard() {
  const { theme } = useAdminTheme();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/api/admin-v2/dashboard/stats')
      .then((res) => setStats(res.data))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Soft auto-refresh every 60 seconds so the dashboard stays live.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className={`text-xs uppercase tracking-widest font-bold ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>Admin</p>
          <h1 className={`text-3xl font-extrabold mt-1 ${tc.textPrimary(theme)}`}>Dashboard</h1>
          <p className={`mt-2 max-w-2xl ${tc.textMuted(theme)}`}>
            Last 7 days vs the previous 7. Refreshes every minute.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className={`text-xs font-semibold px-3 py-2 rounded-lg ${theme === 'light' ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10'}`}
        >
          {loading ? <span className="inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Refreshing…</span> : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {stats && (
        <>
          {/* Top — activity widgets */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              theme={theme}
              label="Leads"
              value={stats.leads?.total ?? 0}
              sub={`${stats.leads?.new ?? 0} unread · ${stats.leads?.last_7d ?? 0} in 7d`}
              icon={Phone}
              to="/admin/leads"
              delta={stats.leads?.delta_pct}
              spark={stats.leads?.spark}
              sparkStroke="#d946ef"
            />
            <MetricCard
              theme={theme}
              label="Number orders"
              value={stats.orders?.last_7d ?? 0}
              sub={`${stats.orders?.prev_7d ?? 0} prev 7d`}
              icon={ShoppingBag}
              to="/admin/orders"
              delta={stats.orders?.delta_pct}
              spark={stats.orders?.spark}
              sparkStroke="#f59e0b"
            />
            <MetricCard
              theme={theme}
              label="Partner apps"
              value={stats.partners?.last_7d ?? 0}
              sub={`${stats.partners?.prev_7d ?? 0} prev 7d`}
              icon={Handshake}
              to="/admin/partners"
              delta={stats.partners?.delta_pct}
              spark={stats.partners?.spark}
              sparkStroke="#0ea5e9"
            />
            <MetricCard
              theme={theme}
              label="Newsletter"
              value={stats.newsletter?.total ?? 0}
              sub={`${stats.newsletter?.last_7d ?? 0} new in 7d`}
              icon={Mail}
              to="/admin/leads"
              delta={stats.newsletter?.delta_pct}
              spark={stats.newsletter?.spark}
              sparkStroke="#10b981"
            />
          </div>

          {/* Content stack */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard theme={theme} label="Pages" value={`${stats.pages?.enabled ?? 0} / ${stats.pages?.total ?? 0}`} sub="enabled / total" icon={FileText} to="/admin/pages" />
            <MetricCard theme={theme} label="SEO coverage" value={stats.seo?.with_metadata ?? 0} sub={`${stats.seo?.missing ?? 0} missing metadata`} icon={Search} to="/admin/seo" />
            <MetricCard theme={theme} label="Blog posts" value={`${stats.blog?.published ?? 0} / ${stats.blog?.total ?? 0}`} sub="published / total" icon={Library} to="/admin/blog" />
            <MetricCard theme={theme} label="Media assets" value={stats.media ?? 0} sub="images & videos" icon={ImageIcon} to="/admin/media" />
          </div>

          {/* Activity feed + tips */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={`lg:col-span-2 rounded-2xl p-5 ${tc.cardBg(theme)}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className={`font-bold ${tc.textPrimary(theme)}`}>Recent activity</h2>
                <Link to="/admin/leads" className="text-xs font-semibold text-violet-600 hover:text-violet-700 inline-flex items-center gap-1">
                  See all leads <ArrowRight size={12} />
                </Link>
              </div>
              <ActivityList items={stats.activity || []} theme={theme} />
            </div>

            <div className="space-y-4">
              <div className={`rounded-2xl p-5 ${tc.cardBg(theme)}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white">
                    <Sparkles size={16} />
                  </div>
                  <h2 className={`font-bold ${tc.textPrimary(theme)}`}>AI everywhere</h2>
                </div>
                <p className={`text-sm ${tc.textMuted(theme)}`}>
                  Every text field gets Generate + Improve. Image fields ship a Pollinations / DALL-E
                  generator. Lead detail pages can auto-summarize incoming enquiries.
                </p>
                <Link to="/admin/ai" className={`inline-flex items-center gap-1 text-sm mt-3 font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
                  Open AI Prompts <ArrowRight size={14} />
                </Link>
              </div>
              <div className={`rounded-2xl p-5 ${tc.cardBg(theme)}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white">
                    <MessageSquareQuote size={16} />
                  </div>
                  <h2 className={`font-bold ${tc.textPrimary(theme)}`}>Lead workflow</h2>
                </div>
                <p className={`text-sm ${tc.textMuted(theme)}`}>
                  The new kanban view on the Leads page groups by status — drag a card to update.
                  Bulk export to CSV is one click away.
                </p>
                <Link to="/admin/leads" className={`inline-flex items-center gap-1 text-sm mt-3 font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}>
                  Open Leads board <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {!stats && !error && (
        <div className={`flex items-center gap-2 text-sm ${tc.textMuted(theme)}`}>
          <Loader2 size={16} className="animate-spin" /> Loading stats…
        </div>
      )}
    </div>
  );
}
